/**
 * Isolated Home live pill row (design spec §5.1, §7; Live Layer v2).
 *
 * Renders EVERY entry of `useLiveEventsRail().entries` through the shared
 * `LivePillRow` shell (§0 width rule, one feed-counts batch, per-appearance
 * impressions — see that module) under the `home_pill` surface. This
 * component calls `useLiveEventsRail` (which ticks via `useNow` every 30s),
 * so it MUST stay an isolated leaf placed between the carousel and the feed —
 * never around the feed list — so the 30s tick re-renders only this bounded
 * subtree, never the feed (spec §3). React Query dedupes this subscription
 * with the carousel's and the recap's into one network request.
 *
 * What stays THIS surface's own:
 *   - `entries` is the always-on rail. ORDER: see `compareEntries` in
 *     `useLiveEventsRail`. The not-going pills are NOT public discovery here:
 *     the rail's one source is `GET /events/my-calendars`, so every pill is an
 *     event in a space the member has already JOINED — simply the ones they have
 *     not RSVP'd to (the merged Meetverse row is where a genuinely public source
 *     joins in). The rail is auth-gated (my-calendars 401s for guests): this row
 *     renders nothing signed-out.
 *   - DISPLAY SHAPING is per phase and never reorders (`capRailForDisplay`):
 *     at most `railLiveMax` live pills — the only surface that bounds the live
 *     block — and at most `railUpcomingMax` upcoming ones, the nearest by start.
 *     Soon is never trimmed. Display-only: `liveSpaceIds` and the carousel rings
 *     still derive from every fetched entry inside the hook.
 *   - No dismissal (v2 item 4): the pill has NO ✕ and is permanent for its
 *     window (upcoming → soon → live → gone at end). RECAP hiding is a
 *     separate surface (hide-on-mark, `useRecapMarkHidden`), untouched.
 */
import { LIVE_EVENTS_CONFIG } from '@config/liveEvents';
import { capRailForDisplay, useLiveEventsRail } from '@hooks/useLiveEventsRail';
import type { LiveSurface } from '@lib/analytics-events';
import { LivePillRow } from './LivePillRow';

/** Analytics surface for every event this slot fires (spec §9). */
const SURFACE: LiveSurface = 'home_pill';

export function LiveHomePill() {
  const { entries, now } = useLiveEventsRail();
  // ONE display shaper, and it never reorders: the row renders the rail in
  // exactly the order the hook handed over, trimmed per phase. Home is the only
  // surface that bounds the LIVE block (`railLiveMax`) — the meetverse row is
  // discovery and caps only its far-future tail. The hook's own partition is
  // untouched either way (rings + signature keep every fetched entry).
  const shown = capRailForDisplay(entries, {
    liveMax: LIVE_EVENTS_CONFIG.railLiveMax,
    upcomingMax: LIVE_EVENTS_CONFIG.railUpcomingMax,
  });
  return <LivePillRow entries={shown} now={now} surface={SURFACE} />;
}
